import React, { useEffect, useState } from 'react';
import { webNavigate } from '../../services/navigate.service';
import { getLocations } from '../../services/inventoryCorrections.service';
import { getClients, getOrdersOverview, OrdersOverview } from '../../services/order.service';
import { useResponsive } from '../../hook/useResponsive';
import { useDebounceEffect } from '../../hook/useDebounceEffect';
import {
  Button,
  DELIVERY_STATUS_GROUPS,
  DocListRow,
  DocumentTable,
  EmptyState,
  ErrorBanner,
  FilterDef,
  FiltersSheet,
  ListState,
  MenuItem,
  ORDER_STATUS,
  PageFrame,
  Pager,
  PageTitle,
  PhoneDocumentList,
  SharePanel,
  StatusStrip,
  ToolbarCard,
  dayMonth,
  money,
  periodTextOf,
  rangeOf,
  readListState,
  statusOf,
  useLanguage,
  useOverviewData,
  writeListState,
} from '../../components/purchasing';

const PAGE_SIZE = 20;
const STATUS_KEYS = DELIVERY_STATUS_GROUPS.map((group) => group.key);

/** Deliveries: the orders sent to suppliers, newest sent first, and what is still to receive. */
const Deliver = () => {
  const preLanguage = localStorage.getItem('language');
  const userId = localStorage.getItem('userId') || '';
  const companyId = localStorage.getItem('companyId') || '';
  const { isMobile: phone } = useResponsive();
  const { language, t } = useLanguage();

  const [state, setState] = useState<ListState>(() => readListState(STATUS_KEYS, ['supplier']));
  const [term, setTerm] = useState(state.search.trim());
  const [sites, setSites] = useState<{ id: string; name: string }[]>([]);
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const update = (patch: Partial<ListState>) => setState((prev) => ({ ...prev, page: 1, ...patch }));
  const setFilter = (key: string, value: string) => setState((prev) => ({ ...prev, page: 1, filters: { ...prev.filters, [key]: value } }));

  useEffect(() => writeListState(state), [state]);
  useDebounceEffect(() => setTerm(state.search.trim()), [state.search], 300);

  useEffect(() => {
    const load = () => {
      getLocations(userId, companyId, 0, 0, '')
        .then((response: any) => setSites((response?.data?.data ?? []).map((row: any) => ({ id: String(row.id), name: String(row.name ?? '') }))))
        .catch(() => undefined);
      // Page 0 / limit 0: every supplier, by name.
      getClients(0, 0, '', companyId, 2)
        .then((response: any) => setSuppliers((response?.data?.data ?? []).map((row: any) => ({ id: String(row.id), name: String(row.companyName ?? '') }))))
        .catch(() => undefined);
    };
    load();
    document.addEventListener('newToken', load);
    return () => document.removeEventListener('newToken', load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, loading, failed, reload } = useOverviewData<OrdersOverview>(
    () =>
      getOrdersOverview({
        view: 'deliveries',
        ...rangeOf(state),
        status: state.status === 'all' ? undefined : state.status,
        search: term || undefined,
        supplierId: state.filters.supplier || undefined,
        locationId: state.site || undefined,
        page: state.page,
        limit: PAGE_SIZE,
      }),
    [state.period, state.from, state.to, state.status, state.site, state.filters.supplier, state.page, term],
    language,
  );

  const siteName = sites.find((site) => site.id === state.site)?.name;
  const periodText = periodTextOf(state, t);
  const open = data?.stillToReceive ?? { orders: 0, lines: 0, value: 0, bySupplier: [] };
  const ordersText = (count: number) => (count === 1 ? t('1 order') : t('{count} orders', { count }));
  const linesText = (count: number) => (count === 1 ? t('1 line') : t('{count} lines', { count }));
  const deliveriesText = (count: number) => (count === 1 ? t('1 delivery') : t('{count} deliveries', { count }));
  const meta = [
    phone ? null : state.site ? siteName : t('All sites'),
    periodText,
    data ? deliveriesText(Number(data.statusCounts?.all ?? 0)) : null,
    data ? t('{amount} still to receive', { amount: money(open.value) }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const counts = data?.statusCounts ?? {};
  const strip = DELIVERY_STATUS_GROUPS.map((group) => ({ key: group.key, label: t(group.label), family: group.family, count: Number(counts[group.key] ?? 0) }));

  const showSite = !state.site;
  const rows: DocListRow[] = (data?.rows ?? []).map((row) => {
    const supplier = row.supplier?.name ?? t('No supplier');
    const site = showSite ? row.location?.name ?? null : null;
    const sent = row.sentAt ? dayMonth(row.sentAt) : '—';
    // Part received says how far it got; the other statuses say it in the pill.
    const note = Number(row.statusId) === ORDER_STATUS.PartiallyReceived ? t('{received} of {count} lines received', { received: row.linesReceived ?? 0, count: row.lines }) : null;
    return {
      id: row.id,
      number: `#${row.internalNumber}`,
      subtitle: row.template?.name ?? null,
      cells: {
        supplier: { main: supplier, sub: site },
        sent: { main: sent },
        total: { main: money(row.total), strong: true },
      },
      status: statusOf(row.statusId),
      statusNote: note,
      phoneText: [supplier, site, row.template?.name].filter(Boolean).join(' · '),
      phoneMeta: [row.sentAt ? t('Sent {date}', { date: sent }) : null, note].filter(Boolean).join(' · '),
      phoneAmount: money(row.total),
    };
  });

  // The order page knows it came from here, for its Back link.
  const hrefOf = (row: DocListRow) => `/${preLanguage}/orders/${row.id}?from=deliveries`;
  const openRow = (row: DocListRow) => webNavigate(hrefOf(row));
  const menuFor = (row: DocListRow): MenuItem[] => [{ key: 'open', label: t('Open order'), icon: 'arrowRight', onSelect: () => openRow(row) }];

  const filters: FilterDef[] = [
    { key: 'site', label: 'Site', icon: 'pin', allLabel: 'All sites', value: state.site, options: sites, onChange: (value) => update({ site: value }) },
    { key: 'supplier', label: 'Supplier', icon: 'truck', allLabel: 'All suppliers', value: state.filters.supplier, options: suppliers, onChange: (value) => setFilter('supplier', value) },
  ];

  const statusLabel = DELIVERY_STATUS_GROUPS.find((group) => group.key === state.status)?.label;
  const empty = term ? (
    <EmptyState icon="search" phone={phone} title={t('No deliveries match "{search}"', { search: term })} desc={t('Check the number, or search by supplier, template or product.')} />
  ) : state.status !== 'all' && statusLabel ? (
    <EmptyState phone={phone} title={t('No deliveries with this status')} desc={t('{status} orders in this period show here.', { status: t(statusLabel) })} />
  ) : (
    <EmptyState
      phone={phone}
      title={t('No deliveries in this period')}
      desc={t('An order shows here once it is sent to the supplier.')}
      action={
        <Button phone={phone} onClick={() => webNavigate(`/${preLanguage}/orderManagement`)}>
          {t('Go to orders')}
        </Button>
      }
    />
  );

  const pager = data ? <Pager phone={phone} t={t} page={state.page} limit={PAGE_SIZE} total={data.count} onPage={(page) => setState((prev) => ({ ...prev, page }))} /> : null;
  const list = phone ? (
    <PhoneDocumentList t={t} label={t('Deliveries')} rows={rows} loading={loading} hrefOf={hrefOf} onOpen={openRow} empty={empty} footer={pager} />
  ) : (
    <DocumentTable
      t={t}
      label={t('Deliveries')}
      numberLabel={t('Order')}
      columns={[
        { key: 'supplier', label: t('Supplier') },
        { key: 'sent', label: t('Sent'), align: 'right', compact: true },
        { key: 'total', label: t('Total'), align: 'right', compact: true },
      ]}
      rows={rows}
      loading={loading}
      hrefOf={hrefOf}
      onOpen={openRow}
      menuFor={menuFor}
      menuLabel={(row) => t('Actions for order {number}', { number: row.number })}
      triggerLabel={(row) => t('More actions for order {number}', { number: row.number })}
      empty={empty}
      footer={pager}
    />
  );

  const panel = (
    <SharePanel
      label={t('Still to receive')}
      title={t('Still to receive')}
      subtitle={data ? t('{orders} · {lines} not received yet', { orders: ordersText(open.orders), lines: linesText(open.lines) }) : undefined}
      empty={t('Everything sent in this period has arrived.')}
      rows={open.bySupplier.map((row) => {
        const share = open.value > 0 ? Math.round((Number(row.value) / open.value) * 100) : 0;
        return { key: row.supplierId, name: row.name ?? t('No supplier'), value: money(row.value), detail: `${linesText(row.lines)} · ${share}%`, share };
      })}
    />
  );

  return (
    <PageFrame phone={phone}>
      <PageTitle phone={phone} t={t} title={t('Deliveries')} meta={meta} />
      <ToolbarCard
        phone={phone}
        t={t}
        period={state.period}
        onPeriod={(period) => update({ period })}
        customFrom={state.from}
        customTo={state.to}
        onCustom={(from, to) => update({ from, to })}
        search={state.search}
        onSearch={(search) => setState((prev) => ({ ...prev, search, page: 1 }))}
        searchLabel={t('Search deliveries')}
        searchPlaceholder={phone ? t('Search deliveries') : t('Number, supplier or product')}
        filters={filters}
        onOpenFilters={() => setFiltersOpen(true)}
      />
      <StatusStrip phone={phone} label={t('Delivery status')} items={strip} active={state.status} onPick={(status) => update({ status })} />
      {failed && <ErrorBanner message={t('The deliveries could not be loaded.')} retryLabel={t('Try again')} onRetry={reload} />}
      {phone ? (
        list
      ) : (
        <div className="pu-split-wide" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(300px, 320px)', gap: '24px', alignItems: 'start' }}>
          <div style={{ minWidth: 0 }}>{list}</div>
          <div style={{ minWidth: 0 }}>{panel}</div>
        </div>
      )}
      {phone && (
        <FiltersSheet
          open={filtersOpen}
          t={t}
          filters={filters}
          showLabel={data?.count === 1 ? t('Show 1 delivery') : t('Show {count} deliveries', { count: data?.count ?? 0 })}
          onClear={() => setState((prev) => ({ ...prev, page: 1, site: '', filters: { ...prev.filters, supplier: '' } }))}
          onClose={() => setFiltersOpen(false)}
        />
      )}
    </PageFrame>
  );
};

export default Deliver;
